import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { Button, Card, Badge, Modal, Input, Select } from '@realvisor/ui';

interface EmailSequenceStep {
  id: string;
  orderIndex: number;
  delayDays: number;
  delayHours: number;
  sendAtTime?: string;
  template: {
    id: string;
    name: string;
    subject: string;
  };
  sentCount: number;
  skippedCount: number;
}

interface EmailSequence {
  id: string;
  name: string;
  description?: string;
  triggerType: 'form_submit' | 'lead_created' | 'stage_changed' | 'manual' | 'contact_created';
  isActive: boolean;
  enrolledCount: number;
  completedCount: number;
  steps: EmailSequenceStep[];
  _count?: {
    enrollments: number;
  };
  createdAt: string;
}

const triggerLabels: Record<string, string> = {
  form_submit: 'Po odeslání formuláře',
  lead_created: 'Po vytvoření leadu',
  stage_changed: 'Po změně fáze',
  manual: 'Ruční spuštění',
  contact_created: 'Po vytvoření kontaktu',
};

const triggerVariants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  form_submit: 'info',
  lead_created: 'success',
  stage_changed: 'primary',
  manual: 'default',
  contact_created: 'warning',
};

export function EmailSequences() {
  const queryClient = useQueryClient();
  const { currentProject, isAllProjects, projects, switchProject } = useProject();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<EmailSequence | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    triggerType: 'form_submit',
  });

  const { data: sequences, isLoading } = useQuery({
    queryKey: ['email-sequences', currentProject?.id],
    queryFn: () => api.get<EmailSequence[]>(`/email-sequences${currentProject ? `?projectId=${currentProject.id}` : ''}`),
    enabled: !!currentProject,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post('/email-sequences', { ...data, projectId: currentProject?.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-sequences', currentProject?.id] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailSequence> }) =>
      api.put(`/email-sequences/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-sequences'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/email-sequences/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-sequences'] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      triggerType: 'form_submit',
    });
  };

  const toggleActive = (sequence: EmailSequence) => {
    updateMutation.mutate({
      id: sequence.id,
      data: { isActive: !sequence.isActive },
    });
  };

  const formatDelay = (days: number, hours: number) => {
    const parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'den' : days < 5 ? 'dny' : 'dní'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hodina' : hours < 5 ? 'hodiny' : 'hodin'}`);
    return parts.join(' ') || 'Ihned';
  };

  // Zobrazit výzvu k výběru projektu pokud je vybráno "Všechny projekty"
  if (isAllProjects) {
    return (
      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            <NavLink
              to="/email/templates"
              className={({ isActive }) =>
                `pb-3 text-sm font-medium border-b-2 ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              Šablony
            </NavLink>
            <NavLink
              to="/email/sequences"
              className={({ isActive }) =>
                `pb-3 text-sm font-medium border-b-2 ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              Sekvence
            </NavLink>
          </nav>
        </div>

        <Card className="p-12">
          <div className="text-center">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Vyberte projekt</h3>
            <p className="mt-2 text-sm text-gray-500">
              Pro správu emailových sekvencí vyberte konkrétní projekt v sidebaru.
            </p>
            {projects.length > 0 && (
              <div className="mt-6">
                <select
                  value=""
                  onChange={(e) => switchProject(e.target.value)}
                  className="block w-full max-w-xs mx-auto rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="" disabled>Vyberte projekt...</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <NavLink
            to="/email/templates"
            className={({ isActive }) =>
              `pb-3 text-sm font-medium border-b-2 ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`
            }
          >
            Šablony
          </NavLink>
          <NavLink
            to="/email/sequences"
            className={({ isActive }) =>
              `pb-3 text-sm font-medium border-b-2 ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`
            }
          >
            Sekvence
          </NavLink>
        </nav>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email sekvence</h1>
          <p className="text-gray-500 mt-1">Automatické follow-up emaily pro vaše kontakty</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nová sekvence
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowPathIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Aktivní sekvence</p>
              <p className="text-xl font-semibold">
                {sequences?.filter((s: EmailSequence) => s.isActive).length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Zapsaných kontaktů</p>
              <p className="text-xl font-semibold">
                {sequences?.reduce((sum: number, s: EmailSequence) => sum + s.enrolledCount, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Dokončených</p>
              <p className="text-xl font-semibold">
                {sequences?.reduce((sum: number, s: EmailSequence) => sum + s.completedCount, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Celkem kroků</p>
              <p className="text-xl font-semibold">
                {sequences?.reduce((sum: number, s: EmailSequence) => sum + s.steps.length, 0) || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sequences List */}
      <div className="space-y-4">
        {sequences?.map((sequence: EmailSequence) => (
          <Card key={sequence.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-medium text-gray-900">{sequence.name}</h3>
                  <Badge variant={sequence.isActive ? 'success' : 'default'}>
                    {sequence.isActive ? 'Aktivní' : 'Neaktivní'}
                  </Badge>
                  <Badge variant={triggerVariants[sequence.triggerType]}>
                    {triggerLabels[sequence.triggerType]}
                  </Badge>
                </div>
                {sequence.description && (
                  <p className="text-gray-500">{sequence.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActive(sequence)}
                >
                  {sequence.isActive ? (
                    <>
                      <PauseIcon className="h-4 w-4 mr-1" />
                      Pozastavit
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4 mr-1" />
                      Aktivovat
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedSequence(sequence)}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Upravit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => {
                    if (confirm('Opravdu chcete smazat tuto sekvenci?')) {
                      deleteMutation.mutate(sequence.id);
                    }
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <UserGroupIcon className="h-4 w-4" />
                {sequence.enrolledCount} zapsaných
              </span>
              <span className="flex items-center gap-1">
                <CheckCircleIcon className="h-4 w-4" />
                {sequence.completedCount} dokončených
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {sequence.steps.length} kroků
              </span>
            </div>

            {/* Steps Timeline */}
            {sequence.steps.length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-4 overflow-x-auto pb-2">
                  {sequence.steps.map((step: EmailSequenceStep, index: number) => (
                    <div key={step.id} className="flex items-center">
                      <div className="flex-shrink-0 w-48 p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                            {index + 1}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDelay(step.delayDays, step.delayHours)}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {step.template.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {step.template.subject}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span>{step.sentCount} odesláno</span>
                          <span>{step.skippedCount} přeskočeno</span>
                        </div>
                      </div>
                      {index < sequence.steps.length - 1 && (
                        <div className="w-8 h-0.5 bg-gray-200 mx-2" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sequence.steps.length === 0 && (
              <div className="border-t pt-4 text-center text-gray-500">
                <p>Tato sekvence nemá žádné kroky.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setSelectedSequence(sequence)}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Přidat krok
                </Button>
              </div>
            )}
          </Card>
        ))}

        {sequences?.length === 0 && (
          <Card className="p-8 text-center">
            <ArrowPathIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Žádné sekvence
            </h3>
            <p className="text-gray-500 mb-4">
              Vytvořte první automatickou email sekvenci pro follow-up.
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Vytvořit sekvenci
            </Button>
          </Card>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        title="Nová sekvence"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <Input
            label="Název sekvence"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Prodejní follow-up"
            required
          />

          <Select
            label="Spouštěč"
            value={formData.triggerType}
            onChange={(value) => setFormData({ ...formData, triggerType: value })}
            options={[
              { value: 'form_submit', label: 'Po odeslání formuláře' },
              { value: 'lead_created', label: 'Po vytvoření leadu' },
              { value: 'stage_changed', label: 'Po změně fáze' },
              { value: 'manual', label: 'Ruční spuštění' },
              { value: 'contact_created', label: 'Po vytvoření kontaktu' },
            ]}
          />

          <Input
            label="Popis"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Automatický follow-up pro nové leady..."
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
              }}
            >
              Zrušit
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              Vytvořit
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Sequence Modal - TODO: Full sequence builder */}
      <Modal
        isOpen={!!selectedSequence}
        onClose={() => setSelectedSequence(null)}
        title={`Upravit: ${selectedSequence?.name}`}
        size="lg"
      >
        <div className="text-center py-8 text-gray-500">
          <p>Plný editor sekvencí bude implementován v další verzi.</p>
          <p className="text-sm mt-2">
            Pro přidání kroků použijte API endpoint POST /email-sequences/{'{id}'}/steps
          </p>
        </div>
      </Modal>
    </div>
  );
}
